import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Feather } from "lucide-react";
import { Reveal, MaskedLine } from "@/components/Reveal";
import { Input } from "@/components/ui/input";
import { useContent } from "@/context/ContentContext";

const COVER_STYLES = [
  "bg-forest text-cream dark:bg-forest-800",
  "bg-sage-100 text-forest dark:bg-forest-900 dark:text-sage",
  "bg-terracotta-100 text-terracotta-600 dark:bg-terracotta-500/15 dark:text-terracotta-400",
];

export default function Blog() {
  const { posts, blogCategories } = useContent();
  const [query, setQuery] = useState("");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return posts.filter((p) => {
      const matchesCategory = category === "All" || p.category === category;
      const matchesQuery =
        !q || p.title.toLowerCase().includes(q) || p.excerpt.toLowerCase().includes(q);
      return matchesCategory && matchesQuery;
    });
  }, [query, category, posts]);

  return (
    <section className="container-site pb-24 pt-16 sm:pt-24" data-testid="blog-page">
      <p className="overline-label">Blog</p>
      <h1 className="mt-5 max-w-3xl font-heading text-4xl font-semibold leading-[1.08] tracking-tight text-foreground sm:text-5xl lg:text-6xl">
        <MaskedLine delay={0.1}>Ideas that respect</MaskedLine>
        <MaskedLine delay={0.25}>
          <span className="text-terracotta-500">the science and the child.</span>
        </MaskedLine>
      </h1>
      <Reveal delay={0.4}>
        <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
          Practical writing on behavior, parenting, and professional practice.
        </p>
      </Reveal>

      <Reveal delay={0.5}>
        <div className="mt-12 flex flex-col gap-4 rounded-3xl border border-border bg-card p-5 shadow-soft sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              data-testid="blog-search-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search articles…"
              className="h-12 rounded-full border-input bg-background pl-11"
              aria-label="Search articles"
            />
          </div>
        </div>
        <div className="mt-5 flex flex-wrap gap-2" role="group" aria-label="Filter by category">
          {["All", ...blogCategories].map((c) => (
            <button
              key={c}
              data-testid={`blog-filter-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCategory(c)}
              className={`rounded-full px-4 py-2 text-xs font-medium transition-colors duration-300 ${
                category === c
                  ? "bg-forest text-cream dark:bg-terracotta-500"
                  : "border border-border text-muted-foreground hover:border-terracotta-500 hover:text-terracotta-500"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </Reveal>

      <p className="mt-8 text-sm text-muted-foreground" data-testid="blog-count">
        {filtered.length} {filtered.length === 1 ? "article" : "articles"}
      </p>

      {filtered.length === 0 ? (
        <div className="mt-10 rounded-3xl border border-dashed border-border p-16 text-center" data-testid="blog-empty">
          <Feather className="mx-auto h-8 w-8 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground">No articles match your search.</p>
        </div>
      ) : (
        <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map((p, i) => (
            <Reveal key={p.slug} delay={(i % 3) * 0.06}>
              <Link
                to={`/blog/${p.slug}`}
                data-testid={`blog-card-${p.slug}`}
                className="card-lift group flex h-full flex-col overflow-hidden rounded-3xl border border-border bg-card shadow-soft"
              >
                <div className={`relative flex h-40 items-end p-6 ${COVER_STYLES[i % 3]}`}>
                  {p.status && p.status !== "published" && (
                    <span className="absolute right-5 top-5 rounded-full bg-black/10 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] dark:bg-white/10">
                      {p.status === "scheduled" ? "Scheduled" : "Draft"}
                    </span>
                  )}
                  <span className="font-heading text-6xl font-semibold opacity-15" aria-hidden="true">
                    {p.title.charAt(0)}
                  </span>
                  <span className="absolute bottom-5 left-6 text-xs font-medium uppercase tracking-[0.18em] opacity-80">
                    {p.category}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-7">
                  <h2 className="font-heading text-lg font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-terracotta-500">
                    {p.title}
                  </h2>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{p.excerpt}</p>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-4 text-xs text-muted-foreground">
                    <span>{new Date(p.date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
                    <span className="flex items-center gap-1.5 font-medium text-forest transition-colors duration-300 group-hover:text-terracotta-500 dark:text-sage dark:group-hover:text-terracotta-400">
                      Read <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" />
                    </span>
                  </div>
                </div>
              </Link>
            </Reveal>
          ))}
        </div>
      )}
    </section>
  );
}
